/**
 * AgentRenewalBanner — field-agent portal header.
 * Phase 2D P4 (2026-04-24)
 *
 * GET /field-activities/renewal-status
 * severity: none | info | warning | urgent | expired
 */
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { AlertTriangle, Info, Clock, Ban } from "lucide-react";

const STYLES = {
  info: { bg: "bg-sky-50", border: "border-sky-200", text: "text-sky-800", Icon: Info },
  warning: { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-800", Icon: Clock },
  urgent: { bg: "bg-rose-50", border: "border-rose-200", text: "text-rose-800", Icon: AlertTriangle },
  expired: { bg: "bg-slate-900", border: "border-slate-900", text: "text-white", Icon: Ban },
};

export default function AgentRenewalBanner() {
  const [status, setStatus] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get("/field-activities/renewal-status").then((res) => {
      if (alive) setStatus(res.data || null);
    }).catch(() => { if (alive) setStatus(null); });
    return () => { alive = false; };
  }, []);

  if (!status) return null;
  if (status.severity === "none") return null;
  const s = STYLES[status.severity] || STYLES.info;
  const Icon = s.Icon;

  return (
    <div
      data-testid={`agent-renewal-banner-${status.severity}`}
      className={`border ${s.border} ${s.bg} ${s.text} rounded-xl px-4 py-3 mb-3 flex items-start gap-3`}
    >
      <Icon className="w-5 h-5 mt-0.5 flex-shrink-0" />
      <div className="flex-1 text-sm">
        <div className="font-semibold" data-testid="agent-renewal-banner-message">
          {status.message || "Contract renewal"}
        </div>
        {status.end_date && (
          <div className="text-xs opacity-80 mt-0.5" data-testid="agent-renewal-banner-meta">
            End: {status.end_date}
            {typeof status.days_remaining === "number" ? ` · ${status.days_remaining} day(s)` : ""}
            {status.renewal_note ? ` · ${status.renewal_note}` : ""}
          </div>
        )}
        {!status.has_contract && (
          <div className="text-xs opacity-80 mt-0.5">No contract on file — contact admin</div>
        )}
      </div>
    </div>
  );
}
